/**
 * useArchivedActivitiesState — la sélection multiple et la restauration des activités archivées.
 *
 * L'écriture est celle du legacy (`ArchivedActivitiesScreen.js:162-186`) :
 * `restoreArchivedActivities` avec `{student}` en route et `{activity_ids}` en corps, puis, au
 * succès, la sélection qui se ferme et la page 1 rechargée (`:117-124`). Le rappel de
 * `getStudent` que le legacy fait en plus ne concerne que les tuteurs (`:127-129`) : en mode
 * parent, le réducteur remet lui-même les activités restaurées dans la liste à faire
 * (`currentUser.js:1626-1650`). Le geste est celui de l'onglet, aux mots près :
 * `useBulkActivityAction`.
 */
import { useMemo } from "react"
import { useNavigation } from "@react-navigation/native"

import { activitiesService } from "../../../../../../data/services/activitiesService"
import { type ApiActivity } from "../../../../adapters/parent/Activities/activityAdapter"
import { type UIActivity } from "../../../types"
import { type ActivityId } from "./activitySelectionReducer"
import { useBulkActivityAction, type BulkActivityWords } from "./useBulkActivityAction"

/** Les mots du legacy, en version parent (`ArchivedActivitiesScreen.js:168-176`). */
const RESTORE_WORDS: BulkActivityWords = {
    confirmTitle: (count) => `Restaurer ${count} activité${count > 1 ? "s" : ""} dans la todo-list ?`,
    confirmMessage: "Ces activités seront ajoutées à la liste d'activités à faire.",
    doneMessage: (count) => `${count} activité${count > 1 ? "s" : ""} restaurée${count > 1 ? "s" : ""}`,
    failedMessage: "Impossible de restaurer. Veuillez réessayer.",
}

export type UseArchivedActivitiesStateArgs = {
    studentId: string | number | null
    activities: UIActivity[]
    /** Recharge la page 1 après une restauration — la liste appartient à `use*Data`. */
    refresh: () => void
    /** Les lignes telles que l'API les sert, pour retrouver celle que le parent touche. */
    raw: ApiActivity[] | null
    /** Ouvre la fiche : l'écran la monte, le crochet ne fait que lui rendre la ligne touchée. */
    onOpenActivity?: (activity: ApiActivity) => void
}

export function useArchivedActivitiesState(args: UseArchivedActivitiesStateArgs) {
    const { studentId, activities, refresh, raw, onOpenActivity } = args
    const navigation = useNavigation<{
        canGoBack: () => boolean
        goBack: () => void
        replace: (name: string) => void
    }>()

    const restore = useBulkActivityAction({
        studentId,
        activities,
        service: activitiesService.restoreArchivedActivities,
        words: RESTORE_WORDS,
        onDone: refresh,
    })

    const bulk = restore.actions
    const actions = useMemo(
        () => ({
            /**
             * La ligne archivée ne vit nulle part dans le store (patron B) : c'est l'écran qui
             * la retrouve dans sa page, et la fiche la reçoit telle quelle.
             */
            openActivity: (id: ActivityId) => {
                const row = (raw ?? []).find((activity) => String(activity?.id) === String(id))
                if (row != null) onOpenActivity?.(row)
            },
            close: () => {
                if (navigation.canGoBack()) navigation.goBack()
                else navigation.replace("menu")
            },
            startSelection: bulk.startSelection,
            toggleSelect: bulk.toggleSelect,
            toggleSelectAll: bulk.toggleSelectAll,
            cancelSelection: bulk.cancelSelection,
            askRestore: bulk.ask,
            cancelRestore: bulk.cancelConfirm,
            confirmRestore: bulk.run,
            dismissSnackbar: bulk.dismissSnackbar,
        }),
        [bulk, navigation, onOpenActivity, raw]
    )

    return {
        selection: restore.selection,
        restoreConfirm: restore.confirm,
        restoring: restore.pending,
        snackbar: restore.snackbar,
        actions,
    }
}
